// Island hydration utilities for selective JavaScript loading
import { lazy, ComponentType } from 'react';

// Track hydrated components to avoid duplicate hydration
const hydratedComponents = new Set<string>();

// Intersection Observer for lazy hydration
let intersectionObserver: IntersectionObserver | null = null;

const getIntersectionObserver = () => {
  if (!intersectionObserver && typeof window !== 'undefined') {
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement;
            const componentId = element.dataset.hydrateId;
            if (componentId && !hydratedComponents.has(componentId)) {
              // Trigger hydration
              element.dispatchEvent(new CustomEvent('hydrate', { 
                detail: { componentId } 
              }));
            }
          }
        });
      },
      { rootMargin: '50px' } // Start loading 50px before element is visible
    );
  }
  return intersectionObserver;
};

// Lazy load heavy components only when needed
export const LazyComponents = {
  EmojiPicker: lazy(() => import('@/components/EmojiPicker')),
} as const;

// Dynamic import wrapper with error handling
export const dynamicImport = async <T extends any>(
  importFn: () => Promise<{ default: ComponentType<T> }>,
  fallbackComponent?: ComponentType<T>
): Promise<ComponentType<T>> => {
  try {
    const module = await importFn();
    return module.default;
  } catch (error) {
    console.warn('Dynamic import failed:', error);
    return (fallbackComponent || (() => null)) as ComponentType<T>;
  }
};

// Hook for lazy hydration of interactive elements
export const useIslandHydration = (componentId: string) => {
  const isHydrated = hydratedComponents.has(componentId);
  
  const markForHydration = (element: HTMLElement) => {
    if (!element || hydratedComponents.has(componentId)) return;
    
    element.dataset.hydrateId = componentId;
    const observer = getIntersectionObserver();
    if (observer) {
      observer.observe(element);
    }
  };

  const hydrateNow = () => {
    hydratedComponents.add(componentId);
  };

  return {
    isHydrated,
    markForHydration,
    hydrateNow,
  };
};

// Static content renderer - renders without JavaScript
export const StaticContent: React.FC<{ 
  children: React.ReactNode;
  fallback: React.ReactNode;
}> = ({ children, fallback }) => {
  // In SSR or initial render, show static content
  // After hydration, show interactive content
  return (
    <div className="static-content">
      <noscript>{fallback}</noscript>
      <div className="js-content">{children}</div>
    </div>
  );
};

// Island wrapper for selective hydration
export const InteractiveIsland: React.FC<{ 
  children: React.ReactNode;
  islandId: string;
  priority?: boolean;
}> = ({ children, islandId, priority = false }) => {
  const { isHydrated, markForHydration } = useIslandHydration(islandId);
  
  return (
    <div 
      ref={(el) => {
        if (el && !priority) {
          markForHydration(el);
        }
      }}
      data-island={islandId}
      className={`interactive-island ${isHydrated ? 'hydrated' : 'static'}`}
    >
      {children}
    </div>
  );
};

// Preload critical components
export const preloadCriticalComponents = () => {
  if (typeof window === 'undefined') return;
  
  // Preload components likely to be used soon
  const criticalImports = [
    () => import('@/components/AuthModal'),
    () => import('@/components/CreatePost'),
  ];
  
  // Use requestIdleCallback to preload during idle time
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      criticalImports.forEach((importFn) => {
        importFn().catch(() => {}); // Ignore errors for preloading
      });
    });
  }
};